import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function VideoUpload() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const videoInputRef = useRef(null);
  const jsonInputRef = useRef(null);

  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoDragOver, setVideoDragOver] = useState(false);
  const [jsonDragOver, setJsonDragOver] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [linkedScript, setLinkedScript] = useState(null);
  const [scriptParsing, setScriptParsing] = useState(false);

  // 파일 확장자별 아이콘
  const getScriptIcon = (ext) => {
    if (ext === 'json') return '📋';
    if (ext === 'pdf') return '📕';
    if (ext === 'docx' || ext === 'doc') return '📘';
    return '📄';
  };

  // 영상 목록 로드
  const loadVideos = useCallback(async () => {
    try {
      const r = await api.get('/video/list');
      setVideos(r.data.videos || []);
    } catch (e) {
      console.error('목록 로드 실패:', e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadVideos(); }, [loadVideos]);

  // 영상 업로드 처리
  const handleVideoUpload = async (file) => {
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['mp4', 'mov'].includes(ext)) {
      setError('MP4, MOV 파일만 업로드 가능합니다');
      return;
    }

    if (file.size > 10 * 1024 * 1024 * 1024) {
      setError('최대 10GB까지 업로드 가능합니다');
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('video', file);

    try {
      await api.post('/video/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      await loadVideos();
    } catch (e) {
      setError(e.response?.data?.error || '업로드 실패');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // 대본 파일 연동 처리 (JSON/PDF/DOCX)
  const handleScriptImport = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    const allowed = ['json', 'pdf', 'docx', 'doc'];
    if (!allowed.includes(ext)) {
      setError('JSON, PDF, DOCX 파일만 업로드 가능합니다');
      return;
    }

    setError('');

    // JSON은 클라이언트에서 직접 파싱
    if (ext === 'json') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          setLinkedScript({ ...data, _fileType: 'json', _fileName: file.name });
        } catch {
          setError('유효하지 않은 JSON 파일입니다');
        }
      };
      reader.readAsText(file);
      return;
    }

    // PDF/DOCX는 서버 파싱 API 호출
    setScriptParsing(true);
    const formData = new FormData();
    formData.append('document', file);

    try {
      const r = await api.post('/script/parse-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLinkedScript({ ...r.data, _fileType: ext, _fileName: file.name });
    } catch (e) {
      setError(e.response?.data?.error || '대본 파일 파싱 실패');
    } finally {
      setScriptParsing(false);
    }
  };

  // 선택 토글
  const toggleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  // 영상 삭제
  const handleDelete = async (id) => {
    if (!window.confirm('이 영상을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/video/${id}`);
      setSelected(prev => prev.filter(v => v !== id));
      await loadVideos();
    } catch (e) {
      setError(e.response?.data?.error || '삭제 실패');
    }
  };

  // 편집 시작
  const startEdit = () => {
    if (selected.length === 0) return;
    const params = new URLSearchParams({ ids: selected.join(',') });
    navigate(`/video/editor/edit?${params.toString()}`);
  };

  // 포맷 유틸
  const formatDuration = (sec) => {
    if (!sec) return '--:--';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-- MB';
    if (bytes > 1024 * 1024 * 1024) return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatResolution = (w, h) => {
    if (!w || !h) return '--';
    if (w >= 3840) return '4K';
    if (w >= 1920) return '1080p';
    if (w >= 1280) return '720p';
    return `${w}x${h}`;
  };

  return (
    <div>
      <h1 style={S.h1}>영상 편집기</h1>
      <p style={S.desc}>영상을 업로드하고 자막 편집, 썸네일 생성을 시작하세요</p>

      {error && <div style={S.errorBox}>{error}</div>}

      {/* 드롭존 2분할: 영상 + 대본 */}
      <div style={S.dropZoneRow}>
        {/* 좌측: 영상 드롭존 */}
        <div
          style={{
            ...S.dropZone,
            ...(videoDragOver ? S.dropZoneActive : {}),
            ...(uploading ? S.dropZoneUploading : {}),
          }}
          onDragOver={(e) => { e.preventDefault(); setVideoDragOver(true); }}
          onDragLeave={() => setVideoDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setVideoDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) handleVideoUpload(file);
          }}
          onClick={() => !uploading && videoInputRef.current?.click()}
        >
          <input
            ref={videoInputRef}
            type="file"
            accept=".mp4,.mov"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) handleVideoUpload(file);
              e.target.value = '';
            }}
            style={{ display: 'none' }}
          />

          {uploading ? (
            <div style={S.uploadingWrap}>
              <div style={S.spinner} />
              <p style={{ color: '#E6EDF3', margin: '16px 0 8px', fontSize: 16, fontWeight: 600 }}>업로드 중...</p>
              <div style={S.progressWrap}>
                <div style={{ ...S.progressBar, width: `${uploadProgress}%` }} />
              </div>
              <p style={{ color: '#4CAF50', fontSize: 14, margin: '8px 0 0' }}>{uploadProgress}%</p>
            </div>
          ) : (
            <div style={S.dropContent}>
              <span style={{ fontSize: 44 }}>🎬</span>
              <p style={S.dropTitle}>영상 파일</p>
              <p style={S.dropSub}>MP4, MOV / 최대 10GB / 4K 지원</p>
              <p style={S.dropHint}>드래그하거나 클릭하여 선택</p>
            </div>
          )}
        </div>

        {/* 우측: 대본 드롭존 */}
        <div
          style={{
            ...S.dropZone,
            ...(jsonDragOver ? S.dropZoneActive : {}),
            ...(linkedScript ? S.dropZoneLinked : {}),
          }}
          onDragOver={(e) => { e.preventDefault(); setJsonDragOver(true); }}
          onDragLeave={() => setJsonDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setJsonDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) handleScriptImport(file);
          }}
          onClick={() => !linkedScript && !scriptParsing && jsonInputRef.current?.click()}
        >
          <input
            ref={jsonInputRef}
            type="file"
            accept=".json,.pdf,.docx,.doc"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) handleScriptImport(file);
              e.target.value = '';
            }}
            style={{ display: 'none' }}
          />

          {scriptParsing ? (
            <div style={S.dropContent}>
              <div style={S.spinnerSmall} />
              <p style={S.dropTitle}>대본 파싱 중...</p>
              <p style={S.dropSub}>파일을 분석하고 있습니다</p>
            </div>
          ) : linkedScript ? (
            <div style={S.dropContent}>
              <span style={{ fontSize: 44 }}>✅</span>
              <p style={{ ...S.dropTitle, color: '#4CAF50' }}>대본 연동 완료</p>
              <p style={S.dropLinkedTitle}>
                {getScriptIcon(linkedScript._fileType || 'json')}{' '}
                {linkedScript.title || linkedScript._fileName || '대본'}
              </p>
              <button
                style={S.unlinkBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setLinkedScript(null);
                }}
              >
                연동 해제
              </button>
            </div>
          ) : (
            <div style={S.dropContent}>
              <span style={{ fontSize: 44 }}>📄</span>
              <p style={S.dropTitle}>대본 파일</p>
              <p style={S.dropSub}>JSON / PDF / Word 파일</p>
              <p style={S.dropHint}>JSON · PDF · DOCX 지원</p>
            </div>
          )}
        </div>
      </div>

      {/* 업로드된 영상 목록 */}
      <div style={{ marginTop: 32 }}>
        <div style={S.listHeader}>
          <h2 style={{ color: '#E6EDF3', fontSize: 18, fontWeight: 600, margin: 0 }}>
            업로드된 영상 ({videos.length})
          </h2>
          {selected.length > 0 && (
            <span style={{ color: '#4CAF50', fontSize: 14 }}>{selected.length}개 선택됨</span>
          )}
        </div>

        {loading ? (
          <div style={S.emptyState}>
            <div style={S.spinner} />
            <p style={{ color: '#8B949E', marginTop: 16 }}>영상 목록 로딩 중...</p>
          </div>
        ) : videos.length === 0 ? (
          <div style={S.emptyState}>
            <span style={{ fontSize: 48, opacity: 0.5 }}>🎥</span>
            <p style={{ color: '#8B949E', marginTop: 12 }}>업로드된 영상이 없습니다</p>
          </div>
        ) : (
          <div style={S.videoGrid}>
            {videos.map(v => {
              const isSelected = selected.includes(v.id);
              return (
                <div key={v.id} style={{ ...S.videoCard, ...(isSelected ? S.videoCardSelected : {}) }}>
                  {/* 썸네일 */}
                  <div style={S.thumbWrap} onClick={() => toggleSelect(v.id)}>
                    {v.thumbnailUrl ? (
                      <img src={v.thumbnailUrl} alt="" style={S.thumbImg} />
                    ) : (
                      <div style={S.thumbPlaceholder}>🎬</div>
                    )}
                    <div style={S.durationBadge}>{formatDuration(v.duration)}</div>
                    <div style={{ ...S.checkBox, ...(isSelected ? S.checkBoxActive : {}) }}>
                      {isSelected ? '✓' : ''}
                    </div>
                  </div>

                  {/* 정보 */}
                  <div style={S.videoInfo}>
                    <p style={S.videoName} title={v.originalName}>{v.originalName}</p>
                    <div style={S.videoMeta}>
                      <span>{formatResolution(v.width, v.height)}</span>
                      <span>{formatSize(v.size)}</span>
                    </div>
                  </div>

                  {/* 삭제 */}
                  <button style={S.deleteBtn} onClick={() => handleDelete(v.id)} title="삭제">
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 편집 시작 버튼 */}
      {videos.length > 0 && (
        <div style={S.bottomBar}>
          <button
            style={{ ...S.primaryBtn, ...(selected.length === 0 ? S.btnOff : {}) }}
            disabled={selected.length === 0}
            onClick={startEdit}
          >
            선택한 영상으로 편집 시작 ({selected.length})
          </button>
        </div>
      )}
    </div>
  );
}

const S = {
  h1: { color: '#E6EDF3', fontSize: 24, fontWeight: 700, marginBottom: 8 },
  desc: { color: '#8B949E', fontSize: 14, marginBottom: 24 },

  errorBox: {
    backgroundColor: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#EF4444',
    borderRadius: 10,
    padding: 16,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
  },

  /* 드롭존 2분할 */
  dropZoneRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  dropZone: {
    border: '2px dashed #2A3441',
    borderRadius: 16,
    padding: '40px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#1E2732',
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropZoneActive: {
    borderColor: '#4CAF50',
    backgroundColor: 'rgba(76,175,80,0.08)',
  },
  dropZoneUploading: {
    cursor: 'default',
    borderColor: '#4CAF50',
  },
  dropZoneLinked: {
    borderColor: '#2E7D32',
    borderStyle: 'solid',
    backgroundColor: 'rgba(46,125,50,0.06)',
  },
  dropContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dropTitle: {
    color: '#E6EDF3',
    fontSize: 17,
    fontWeight: 600,
    margin: '14px 0 6px',
  },
  dropSub: {
    color: '#8B949E',
    fontSize: 13,
    margin: '0 0 4px',
  },
  dropHint: {
    color: '#6E7681',
    fontSize: 12,
    margin: '6px 0 0',
  },
  dropLinkedTitle: {
    color: '#C9D1D9',
    fontSize: 14,
    margin: '4px 0 12px',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  unlinkBtn: {
    padding: '6px 16px',
    backgroundColor: 'transparent',
    border: '1px solid #2A3441',
    borderRadius: 8,
    color: '#8B949E',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR',sans-serif",
  },
  uploadingWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  progressWrap: {
    width: 200,
    height: 6,
    backgroundColor: '#0D1117',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#4CAF50',
    borderRadius: 3,
    transition: 'width 0.3s',
  },

  /* 목록 */
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '48px 0',
  },

  /* 그리드 */
  videoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 16,
  },
  videoCard: {
    backgroundColor: '#1E2732',
    borderRadius: 16,
    border: '1px solid #2A3441',
    overflow: 'hidden',
    position: 'relative',
    transition: 'border-color 0.15s',
  },
  videoCardSelected: {
    borderColor: '#4CAF50',
    boxShadow: '0 0 12px rgba(76,175,80,0.15)',
  },

  /* 썸네일 */
  thumbWrap: {
    width: '100%',
    height: 160,
    backgroundColor: '#0D1117',
    position: 'relative',
    cursor: 'pointer',
    overflow: 'hidden',
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 48,
    opacity: 0.3,
  },
  durationBadge: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0,0,0,0.75)',
    color: '#fff',
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
  },
  checkBox: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 24,
    height: 24,
    borderRadius: 6,
    border: '2px solid #8B949E',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
  },
  checkBoxActive: {
    borderColor: '#4CAF50',
    backgroundColor: '#2E7D32',
  },

  /* 영상 정보 */
  videoInfo: {
    padding: '12px 14px',
  },
  videoName: {
    color: '#E6EDF3',
    fontSize: 14,
    fontWeight: 500,
    margin: '0 0 6px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  videoMeta: {
    display: 'flex',
    gap: 12,
    color: '#8B949E',
    fontSize: 12,
  },

  /* 삭제 */
  deleteBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.6)',
    border: 'none',
    color: '#8B949E',
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* 하단 */
  bottomBar: {
    marginTop: 32,
    display: 'flex',
    justifyContent: 'center',
  },
  primaryBtn: {
    padding: '14px 40px',
    backgroundColor: '#2E7D32',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR',sans-serif",
  },
  btnOff: {
    backgroundColor: '#1E2732',
    color: '#555',
    cursor: 'not-allowed',
  },

  spinner: {
    width: 48,
    height: 48,
    border: '4px solid #2A3441',
    borderTop: '4px solid #4CAF50',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  spinnerSmall: {
    width: 32,
    height: 32,
    border: '3px solid #2A3441',
    borderTop: '3px solid #4CAF50',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};
